import { useState } from "react";
import { DeviceConfigService } from "../services/deviceConfigService";
import airplaneWing from "../assets/airplaneWing.jpg";

interface ConfigureDevicePageProps {
  onConfigured: () => void;
}

type OwnerType = "AIRPORT" | "CLIENT";

const ConfigureDevicePage = ({ onConfigured }: ConfigureDevicePageProps) => {
  const [ownerType, setOwnerType] = useState<OwnerType | null>(null);
  const [locationCode, setLocationCode] = useState("");
  const [email, setEmail] = useState("");
  const [posAviation, setPosAviation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (!ownerType) {
        throw new Error("Please select device type");
      }

      if (!locationCode.trim()) {
        throw new Error("Location code is required");
      }

      if (ownerType === "CLIENT" && !email.trim()) {
        throw new Error("Email is required for client registration");
      }

      let result;
      if (ownerType === "AIRPORT") {
        result = await DeviceConfigService.registerAirport(locationCode);
      } else {
        result = await DeviceConfigService.registerClient(locationCode, email);
        DeviceConfigService.setPosAviation(posAviation);
      }

      setSuccess(
        `Device registered successfully! Device Code: ${result.data.device.deviceCode}`
      );

      // Auto-login after successful registration
      setTimeout(async () => {
        try {
          await DeviceConfigService.login();
          onConfigured();
        } catch (loginError) {
          console.error("Auto-login failed:", loginError);
          setError("Device registered but login failed. Reloading...");
          setTimeout(() => window.location.reload(), 2000);
        }
      }, 1500);
    } catch (err: any) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to register device");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setOwnerType(null);
    setLocationCode("");
    setEmail("");
    setPosAviation(false);
    setError("");
    setSuccess("");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundImage: `url(${airplaneWing})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.95)",
          borderRadius: "12px",
          padding: "40px",
          maxWidth: "500px",
          width: "100%",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
        }}
      >
        <h1
          style={{
            fontSize: "2rem",
            fontWeight: "bold",
            marginBottom: "10px",
            color: "#1a1a1a",
            textAlign: "center",
          }}
        >
          Configure Device
        </h1>
        <p
          style={{
            fontSize: "0.95rem",
            color: "#666",
            marginBottom: "30px",
            textAlign: "center",
          }}
        >
          Register this device to start using FIDS
        </p>

        {!ownerType ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
            <button
              onClick={() => setOwnerType("AIRPORT")}
              style={{
                padding: "20px",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "white",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              ✈️ Register as Airport
            </button>
            <button
              onClick={() => setOwnerType("CLIENT")}
              style={{
                padding: "20px",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "white",
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(245, 87, 108, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              🏢 Register as Client
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Device Type
              </label>
              <div
                style={{
                  padding: "12px",
                  background: "#f0f0f0",
                  borderRadius: "6px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontWeight: "600", color: "#555" }}>
                  {ownerType === "AIRPORT" ? "✈️ Airport" : "🏢 Client"}
                </span>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: "6px 12px",
                    fontSize: "0.85rem",
                    color: "#667eea",
                    background: "white",
                    border: "1px solid #667eea",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  Change
                </button>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                htmlFor="locationCode"
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#333",
                }}
              >
                Location Code *
              </label>
              <input
                id="locationCode"
                type="text"
                value={locationCode}
                onChange={(e) => setLocationCode(e.target.value)}
                placeholder={ownerType === "AIRPORT" ? "e.g., KUL-L0001" : "e.g., L0008"}
                required
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "1rem",
                  border: "2px solid #e0e0e0",
                  borderRadius: "6px",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#667eea")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
              />
              <p style={{ fontSize: "0.85rem", color: "#666", marginTop: "5px" }}>
                {ownerType === "AIRPORT"
                  ? "Format: AIRPORT_CODE-LOCATION (e.g., KUL-L0001)"
                  : "Your assigned location code"}
              </p>
            </div>

            {ownerType === "CLIENT" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="email"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#333",
                  }}
                >
                  Email Address *
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "1rem",
                    border: "2px solid #e0e0e0",
                    borderRadius: "6px",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = "#667eea")}
                  onBlur={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
                />
              </div>
            )}

            {ownerType === "CLIENT" && (
              <div style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="posAviation"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "15px",
                    padding: "12px",
                    background: "#f9f9f9",
                    border: "2px solid #e0e0e0",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                >
                  <span>
                    <span style={{ display: "block", fontWeight: "600", color: "#333" }}>
                      POS-AVIATION
                    </span>
                    <span style={{ fontSize: "0.85rem", color: "#666" }}>
                      Enable aviation features for this device
                    </span>
                  </span>
                  <span
                    style={{
                      position: "relative",
                      flexShrink: 0,
                      width: "48px",
                      height: "26px",
                      borderRadius: "13px",
                      background: posAviation ? "#f5576c" : "#ccc",
                      transition: "background 0.2s",
                    }}
                  >
                    <input
                      id="posAviation"
                      type="checkbox"
                      role="switch"
                      checked={posAviation}
                      onChange={(e) => setPosAviation(e.target.checked)}
                      style={{
                        position: "absolute",
                        opacity: 0,
                        width: "100%",
                        height: "100%",
                        margin: 0,
                        cursor: "pointer",
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        top: "3px",
                        left: posAviation ? "25px" : "3px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: "white",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                        transition: "left 0.2s",
                        pointerEvents: "none",
                      }}
                    />
                  </span>
                </label>
              </div>
            )}

            {error && (
              <div
                style={{
                  padding: "12px",
                  marginBottom: "20px",
                  background: "#fee",
                  border: "1px solid #fcc",
                  borderRadius: "6px",
                  color: "#c33",
                  fontSize: "0.9rem",
                }}
              >
                ❌ {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  padding: "12px",
                  marginBottom: "20px",
                  background: "#efe",
                  border: "1px solid #cfc",
                  borderRadius: "6px",
                  color: "#3c3",
                  fontSize: "0.9rem",
                }}
              >
                ✅ {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "white",
                background: loading
                  ? "#ccc"
                  : ownerType === "AIRPORT"
                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  : "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                border: "none",
                borderRadius: "8px",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!loading) e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {loading ? "Registering..." : "Register Device"}
            </button>
          </form>
        )}

        <div
          style={{
            marginTop: "30px",
            padding: "15px",
            background: "#f9f9f9",
            borderRadius: "6px",
            fontSize: "0.85rem",
            color: "#666",
          }}
        >
          <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#333" }}>
            ℹ️ Note:
          </p>
          <ul style={{ margin: 0, paddingLeft: "20px" }}>
            <li>Your device credentials will be stored securely on this device</li>
            <li>You can reset configuration at any time</li>
            <li>Contact support if you need assistance</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConfigureDevicePage;